import {
  BikeRoute,
  BikeRouteService,
  DeepLinkService,
  IPhoto,
  Parking,
  PhotosDialog,
  Poi,
  Report,
  Ride,
  RideService,
  useCancellablePromise,
  useUnits,
} from '@geovelo-frontends/commons';
import { useTheme } from '@mui/material/styles';
import { graphql, navigate } from 'gatsby';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BikeRouteQuery } from '../../../graphql-types';
import defaultBikeRouteImage from '../../assets/images/bike-route.jpg';
import { TripsIcon } from '../../components/icons';
import ParkingLots, { TParkingLotsRef } from '../../components/map-markers/parking-lots';
import Pois, { TPoisRef } from '../../components/map-markers/pois';
import Reports, { TReportsRef } from '../../components/map-markers/reports';
import Seo from '../../components/seo';
import { AppContext, defaultSmallDeviceShowDetailsAction } from '../../context';
import useBikeRoutes from '../../hooks/map/bike-routes';
import useQueryParams from '../../hooks/query-params';
import DetailsLayout from '../../layouts/app-content/details';
import { TPageProps } from '../../page-props';
import { parseBikeRoute } from '../../utils/bike-route';
import { parseRide } from '../../utils/ride';

import DetailsTab from './details-tab';
import StepsTab from './steps-tab';

function BikeRouteDetailsPage(props: TPageProps<BikeRouteQuery>): React.JSX.Element {
  const {
    location,
    path,
    params: { id },
    data: { bikeRoute: _bikeRoute, bikeRouteRides },
  } = props;
  const [bikeRoute, setBikeRoute] = useState<BikeRoute | null | undefined>(
    (_bikeRoute?.data && parseBikeRoute(_bikeRoute.data)) || undefined,
  );
  const { get: getQueryParams, update: updateQueryParams } = useQueryParams(location.search);
  const [selectedTab, selectTab] = useState<'details' | 'steps'>(() => {
    const { tab } = getQueryParams();

    return tab === 'steps' ? 'steps' : 'details';
  });
  const [rides, setRides] = useState<Ride[]>();
  const [showedPhotos, showPhotos] = useState<IPhoto[] | null>(null);
  const [selectedParkingLot, selectParkingLot] = useState<Parking | null>(null);
  const [selectedPoi, selectPoi] = useState<Poi | null>(null);
  const [selectedReport, selectReport] = useState<Report | null>(null);
  const [photosDialogOpen, openPhotosDialog] = useState(false);
  const parkingLotsRef = useRef<TParkingLotsRef>(null);
  const poisRef = useRef<TPoisRef>(null);
  const reportsRef = useRef<TReportsRef>(null);
  const {
    map: { current: map },
    actions: { setSmallDeviceShowDetailsAction, setSmallDeviceRedirectAction },
  } = useContext(AppContext);
  const {
    t,
    i18n: { language: currentLanguage },
  } = useTranslation();
  const theme = useTheme();
  const {
    initialized: mapInitialized,
    init: initMap,
    add: addOnMap,
    clear: clearMap,
  } = useBikeRoutes(map, { detailed: true, onClick: handleStepSelect });
  const { cancellablePromise, cancelPromises } = useCancellablePromise();
  const { toDistance } = useUnits();

  useEffect(() => {
    if (!_bikeRoute?.data) getBikeRoute(parseInt(id));

    setSmallDeviceShowDetailsAction({ labelKey: 'commons.actions.see_details', Icon: TripsIcon });

    return () => {
      setSmallDeviceShowDetailsAction(defaultSmallDeviceShowDetailsAction);
      cancelPromises();
      clearMap();
    };
  }, []);

  useEffect(() => {
    if (map) initMap();
  }, [map]);

  useEffect(() => {
    if (mapInitialized && bikeRoute && rides) addOnMap(bikeRoute, rides);
  }, [mapInitialized, bikeRoute, rides]);

  useEffect(() => {
    if (selectedParkingLot) {
      poisRef.current?.unselect();
      reportsRef.current?.unselect();
    }
  }, [selectedParkingLot]);

  useEffect(() => {
    if (selectedPoi) {
      parkingLotsRef.current?.unselect();
      reportsRef.current?.unselect();
    }
  }, [selectedPoi]);

  useEffect(() => {
    if (selectedReport) {
      poisRef.current?.unselect();
      parkingLotsRef.current?.unselect();
    }
  }, [selectedReport]);

  useEffect(() => {
    if (bikeRoute) {
      getRides();

      const deepLinkCallback = DeepLinkService.getCallback({
        androidLink: `ride-sets/${id}`,
      });
      if (deepLinkCallback) {
        setSmallDeviceRedirectAction({
          labelKey: 'geovelo.actions.see_steps_on_app',
          callback: deepLinkCallback,
        });
      }
    } else if (bikeRoute === null) navigate(`/${currentLanguage}/trips`);

    return () => {
      setSmallDeviceRedirectAction();
    };
  }, [bikeRoute]);

  useEffect(() => {
    updateQueryParams({ tab: selectedTab });
  }, [selectedTab]);

  async function getBikeRoute(id: number) {
    try {
      const bikeRoute = await cancellablePromise(BikeRouteService.getBikeRoute(id));

      setBikeRoute(bikeRoute);
    } catch (err) {
      if (
        !(err instanceof Error) ||
        err?.name !== 'CancelledPromiseError' ||
        (typeof err === 'object' && err && 'detail' in err)
      ) {
        console.error(err);
        setBikeRoute(null);
      }
    }
  }

  async function getRides() {
    if (bikeRouteRides?.nodes) {
      setRides(
        bikeRouteRides.nodes.reduce<Ride[]>((res, node) => {
          if (node.data) res.push(parseRide(node.data));

          return res;
        }, []),
      );
      return;
    }

    if (!bikeRoute) return;

    const ids = bikeRoute.rides.map(({ rideId }) => rideId);

    try {
      const { rides: _rides } = await cancellablePromise(
        RideService.getRides({
          page: 1,
          pageSize: ids.length,
          ids,
          query:
            '{id, title, geo_point_a, geo_point_a_title, geo_point_b, geo_point_b_title, geometry_condensed, duration, route_duration, distance, photos{thumbnailSquare, thumbnail}, themes}',
        }),
      );

      setRides(_rides);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') setRides([]);
    }
  }

  function handlePhotosDialogOpen() {
    if (!bikeRoute || bikeRoute.photos.length === 0) return;
    showPhotos(bikeRoute.photos);
    openPhotosDialog(true);
  }

  function handlePhotosDialogClose() {
    openPhotosDialog(false);
    setTimeout(() => showPhotos(null), theme.transitions.duration.leavingScreen);
  }

  function handleStepSelect(id: number) {
    navigate(`/${currentLanguage}/rides/${id}`);
  }

  const description =
    (bikeRoute &&
      `${toDistance(bikeRoute.distance || 0).replace(/\u00a0/g, ' ')} | ${t(
        'geovelo.bike_routes.list_item.steps',
        { count: bikeRoute.stepsCount },
      )}${bikeRoute.description ? ` | ${bikeRoute.description}` : ''}`) ||
    '';

  return (
    <>
      {bikeRoute ? (
        <Seo
          description={
            description.length > 300 ? `${description.substring(0, 297)}...` : description
          }
          imageUrl={bikeRoute.photos[0]?.thumbnailUrl || defaultBikeRouteImage}
          title={`${bikeRoute.title} - ${t('geovelo.bike_routes.detail.title')}`}
          {...props}
        />
      ) : (
        <Seo title={`${t('geovelo.bike_routes.detail.title')}`} {...props} />
      )}
      <DetailsLayout
        disableContentPadding
        headerProps={{
          backgroundColor: 'whitesmoke',
          backgroundImage: bikeRoute
            ? bikeRoute.photos[0]?.thumbnailUrl || defaultBikeRouteImage
            : undefined,
          onClick: bikeRoute?.photos[0] ? handlePhotosDialogOpen : undefined,
          size: 'large',
        }}
        selectedTab={selectedTab}
        selectTab={selectTab}
        tabs={[
          { key: 'details', label: t('commons.details') },
          {
            key: 'steps',
            label: t('geovelo.bike_routes.detail.navigation.steps'),
            hidden: !bikeRoute || bikeRoute.rides.length === 0,
          },
        ]}
        title={bikeRoute?.title}
      >
        {selectedTab === 'details' && <DetailsTab bikeRoute={bikeRoute} rides={rides} />}
        {selectedTab === 'steps' && <StepsTab bikeRoute={bikeRoute} rides={rides} />}
      </DetailsLayout>
      <PhotosDialog
        onClose={handlePhotosDialogClose}
        open={photosDialogOpen}
        photos={showedPhotos}
      />
      <ParkingLots onSelectedParkingLotChange={selectParkingLot} ref={parkingLotsRef} />
      <Pois onSelectedPoiChange={selectPoi} ref={poisRef} />
      <Reports onSelectedReportChange={selectReport} path={path} ref={reportsRef} />
    </>
  );
}

export const query = graphql`
  fragment BikeRouteData on bikeRouteData {
    id
    area_wide
    creator
    description
    distance
    geo_point_icon {
      coordinates
      type
    }
    geometry_condensed {
      coordinates
      type
    }
    icon
    icon_alt
    medias {
      copyright
      description
      id
      rideset
      title
      type
      url
    }
    photos {
      copyright
      description
      id
      thumbnail
      thumbnailSquare
      title
      url
    }
    publication_status
    rides {
      created
      order_atob
      order_btoa
      priority
      ride_atob_next
      ride_atob_previous
      ride_btoa_next
      ride_btoa_previous
      ride_id
      updated
    }
    theme_primary_color
    theme_secondary_color
    title
  }
  fragment BikeRouteRideData on rideData {
    id
    title
    geo_point_a {
      coordinates
      type
    }
    geo_point_a_title
    geo_point_b {
      coordinates
      type
    }
    geo_point_b_title
    geometry_condensed {
      coordinates
      type
    }
    duration
    route_duration
    distance
    vertical_gain
    photos {
      thumbnailSquare
      thumbnail
    }
    themes
  }
`;

export default BikeRouteDetailsPage;
